import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Message,
  Page,
  Row,
  Typography,
  Upload,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Close, DownloadOutlined, FileText, PenTool } from "@payping/icons";
import { DashboardRoutes } from "@/router";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SubLayout } from "@/components/SubLayout";
import { cancelId, closeId, continueId, fileDownloadId, fileUploadId } from "@/assets/googleTrackIds";
import { buildUrl } from "@payping/utilities-shared/url";
import { useAsync } from "react-use";
import { BankAgreementsApis, BankTypeEnum, uploadApis } from "@payping/api";
import { useState } from "react";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { getBankAgreements } from "@payping/redux/actions";
import styles from "../../assets/scss/DigitalAccount/digital.module.scss";
import { URLS } from "@payping/utilities-shared/consts";

const TermsAndConditions = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { bankAgreement } = useAppSelector((state) => state.digitalAccount);
  const { bank } = useParams<{ bank: string }>() as { bank: string };
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedAgreementPath, setUploadedAgreementPath] = useState<string>();
  const dispatch = useAppDispatch();

  const onChange = async (info: UploadChangeParam) => {
    const { status } = info.file;
    if (status === "done") {
      Message.success({ content: `${info.file.name} file uploaded successfully.` });
    } else if (status === "error") {
      Message.error({ content: `${info.file.name} file upload failed.` });
    }
    setFileList(info.fileList);
  };

  useAsync(async () => {
    await BankAgreementsApis.createAgreementWithRequestModelWithCancellationTokenPost({
      bankType: BankTypeEnum[bank as unknown as BankTypeEnum] as unknown as BankTypeEnum,
    });
    await dispatch(getBankAgreements());
  }, []);

  const [{ loading }, onSubmit] = useMessagedAsyncFn(async () => {
    const agreementId = bankAgreement.find((value) => value.agreementId)?.agreementId;
    await BankAgreementsApis.acceptAgreementWithRequestModelWithCancellationTokenPost({
      agreementId,
      agreementFilePath: uploadedAgreementPath,
    });
    await dispatch(getBankAgreements());
    navigate(DashboardRoutes.digitalAccount.yourAccount.url);
  }, [bankAgreement, uploadedAgreementPath]);

  return (
    <Drawer
      title={<Typography.Text>{t("digitalAccount.termsAndCondition.samanBankAccountOpeningForm")}</Typography.Text>}
      placement={"bottom"}
      height={"100%"}
      closeIcon={false}
      open={true}
      extra={
        <Link to={DashboardRoutes.digitalAccount.home?.url}>
          <Button type={"text"} icon={<Close width={14} height={14} />} data-google-track={closeId} />
        </Link>
      }
    >
      <Page>
        <SubLayout>
          <Row gutter={16}>
            <Col xs={24} lg={8}>
              <Card.Ant>
                <Flex vertical gap={16}>
                  <div className="background--gray100 !tw-flex !tw-justify-center tw-p-4">
                    <Flex gap={16}>
                      <PenTool width={84} />
                      <FileText width={84} />
                    </Flex>
                  </div>
                  <Typography.Title level={5} className={"tw-mb-0"}>
                    {t("digitalAccount.termsAndCondition.payPingContractWithYouSamanBank")}
                  </Typography.Title>
                  <Typography.Text>{t("digitalAccount.termsAndCondition.contractDesc")}</Typography.Text>
                  <Button
                    target={"_blank"}
                    href={`${buildUrl(URLS.STORAGE_URL, "digitalAccount/documents/agreementDigitalAccountSaman.pdf").toString()}`}
                    icon={<DownloadOutlined width={14} height={14} />}
                    type={"default"}
                    data-google-track={fileDownloadId}
                  >
                    {t("digitalAccount.termsAndCondition.receiveTheContractFile")}
                  </Button>
                  <Typography.Text type={"secondary"}>
                    {t("digitalAccount.termsAndCondition.contractApprovalConditions")}
                  </Typography.Text>
                  <ul style={{ listStyleType: "square" }} className="tw-pr-4">
                    <li>{t("digitalAccount.termsAndCondition.contractApprovalConditionsDesc1")}</li>
                    <li>{t("digitalAccount.termsAndCondition.contractApprovalConditionsDesc2")}</li>
                    <li>{t("digitalAccount.termsAndCondition.contractApprovalConditionsDesc3")}</li>
                  </ul>
                  <Flex gap={16}>
                    <Button loading={loading} type={"primary"} block data-google-track={continueId} onClick={onSubmit}>
                      {t("continue")}
                    </Button>
                    <Link to={DashboardRoutes.digitalAccount.yourAccount.url}>
                      <Button type={"default"} block data-google-track={cancelId}>
                        {t("cancel")}
                      </Button>
                    </Link>
                  </Flex>
                </Flex>
              </Card.Ant>
            </Col>
            <Col xs={24} lg={16}>
              <Upload.Dragger
                allowedExtensions={["pdf", "zip"]}
                onChange={onChange}
                fileList={fileList}
                name={"file"}
                maxCount={1}
                showUploadList={{ showRemoveIcon: true }}
                requestFunction={async (file) => {
                  const filename = await uploadApis.digitalAccountContractWithFilePost(file);
                  setUploadedAgreementPath(filename);
                  return filename;
                }}
                className={styles.upload_condition}
                data-google-track={fileUploadId}
              >
                <div className="!tw-flex !tw-justify-center">
                  <Flex vertical gap={8}>
                    <div className="!tw-flex !tw-justify-center">
                      <FileText width={54} />
                    </div>
                    <Typography.Text strong>{t("digitalAccount.termsAndCondition.uploadContract")}</Typography.Text>
                    <Typography.Link>
                      <Flex gap={8} className="!tw-flex !tw-justify-center">
                        <DownloadOutlined width={12} />
                        {t("selectFile")}
                      </Flex>
                    </Typography.Link>
                  </Flex>
                </div>
              </Upload.Dragger>
            </Col>
          </Row>
        </SubLayout>
      </Page>
    </Drawer>
  );
};

export default TermsAndConditions;
